import { Injectable, inject } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { filter } from "rxjs";
import { ConsentsDto } from "src/app/api/smart-pensjon-rest-api/models";
import { OnboardingService, OnboardingStepKey } from "src/app/services/onboarding.service";
import { LARGE_DIALOG_WINDOW_WIDTH } from "src/app/constants/technical.constants";
import { GroupAndMarketingConsentsComponent } from "src/app/modules/shared/components/group-and-marketing-consents/group-and-marketing-consents.component";
import { STOREBRAND_INTRA_PROD_URL, STOREBRAND_PROD_URL } from "src/app/constants/business.constants";
import { DialogService } from "src/app/services/dialog/dialog.service";
import { UtmService } from "src/app/services/utm.service";
import { SavingsWebExitClient } from "src/app/utils/createSavingsWebUrl";
import { toReadableQueryString } from "src/app/utils/readable-query";
import { ConsentFlow, OnboardingDecisionService } from "src/app/services/onboarding-decision.service";

@Injectable({ providedIn: "root" })
export class NavigateToService {
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly onboardingDecisionService = inject(OnboardingDecisionService);
  private readonly utmService = inject(UtmService);

  /**
   * True while a click is being decided, which is the REST calls. Other clicks are ignored meanwhile.
   * Nothing is loaded before the popup: its steps load their own REST data and show a skeleton.
   */
  private isDeciding = false;

  /** Identifies the current decision. A navigation started meanwhile bumps it, which makes the decision stale. */
  private decisionId = 0;

  /** `readConsents` is a GET /user/consents answer read earlier in the same user action, see fetchOnboardingSteps. */
  public navigateToUrlWithOnboarding(url: string, readConsents?: ConsentsDto): void {
    void this.decideOnce((isCurrent) => this.openOnboardingOrNavigate(url, isCurrent, readConsents));
  }

  public navigateToUrlWithOnlyConsentOnboarding(url: string): void {
    void this.decideOnce((isCurrent) => this.openConsentOnboardingOrNavigate(url, isCurrent));
  }

  public navigateWithExitUrl(route: string, exitUrl = this.router.url): Promise<boolean> {
    const exitUrlWithoutQueryParams = exitUrl.split("?")?.[0];

    return this.router.navigate([route], {
      queryParams: { exitUrl: exitUrlWithoutQueryParams },
      queryParamsHandling: "merge",
    });
  }

  public navigateToExternalUrl(url: string, target: "_blank" | "_self" = "_blank"): void {
    const queryParams = new URLSearchParams(window.location.search);

    const utmFields = this.utmService.getUTMFieldsFromLocalStorage();

    Object.entries(utmFields).forEach(([key, value]) => {
      if (typeof value === "string") {
        queryParams.set(key, value);
      }
    });

    queryParams.set("referrer", SavingsWebExitClient.MinPensjon);
    const isAdvisorMode = window.location.hostname === STOREBRAND_INTRA_PROD_URL;

    // Parse the original URL
    const parsedUrl = new URL(url);
    const isStorebrandHostname = parsedUrl.hostname === STOREBRAND_PROD_URL;

    const targetUrl = new URL(
      isAdvisorMode && isStorebrandHostname ? url.replace(parsedUrl.hostname, STOREBRAND_INTRA_PROD_URL) : url,
    );

    // Added as a readable string. targetUrl.searchParams would encode the link's own query again
    // (exitUrl=https://... becomes https%3A%2F%2F...), and min-sparing's links would then get "%25".
    const query = [targetUrl.search.slice(1), toReadableQueryString(queryParams)].filter(Boolean).join("&");

    window.open(`${targetUrl.origin}${targetUrl.pathname}?${query}${targetUrl.hash}`, target);
  }

  /**
   * A double click must not open the popup twice. The decision waits for a request, so the popup isn't
   * open yet to block the second click. The flag is cleared once the popup, the dialog or the navigation
   * has started.
   *
   * If the user navigates somewhere else while the decision waits, the decision goes stale: it must not
   * navigate or open a popup over the newer page. The NavigationStart makes it stale and frees the flag,
   * so the next click is decided. A stale decision leaves the flag alone, because a newer click may own it.
   * Only navigations that start after this point count, so a caller's own awaited navigation before the
   * click doesn't cancel it.
   */
  private async decideOnce(decide: (isCurrent: () => boolean) => Promise<void>): Promise<void> {
    if (this.isDeciding) {
      return;
    }

    const id = this.decisionId + 1;
    this.decisionId = id;
    const isCurrent = (): boolean => this.decisionId === id;
    this.isDeciding = true;
    const navigationStarts = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (isCurrent()) {
          this.decisionId = id + 1;
          this.isDeciding = false;
        }
      });
    try {
      await decide(isCurrent);
    } finally {
      navigationStarts.unsubscribe();
      if (isCurrent()) {
        this.isDeciding = false;
      }
    }
  }

  /**
   * Opens the popup with the steps that OnboardingDecisionService decides. When there are none, and when
   * the requests fail, it navigates. The popup opens straight away, because the steps load their own REST
   * data and show a skeleton until it arrives. Does nothing once the decision is stale.
   */
  private async openOnboardingOrNavigate(
    url: string,
    isCurrent: () => boolean,
    readConsents: ConsentsDto | undefined,
  ): Promise<void> {
    const stepKeys = (await this.onboardingDecisionService.fetchOnboardingSteps(readConsents)) ?? [];
    if (!isCurrent()) {
      return;
    }

    // open() with no steps falls back to the default steps, so navigate instead.
    if (stepKeys.length === 0) {
      this.router.navigate([url], {
        queryParamsHandling: "merge",
      });
      return;
    }

    this.navigateWithOnboardingSteps(url, stepKeys);
  }

  /**
   * Shows the consent popup that OnboardingDecisionService decides. Otherwise, and when the request fails,
   * it navigates. Does nothing once the decision is stale.
   */
  private async openConsentOnboardingOrNavigate(url: string, isCurrent: () => boolean): Promise<void> {
    const consentFlow = await this.onboardingDecisionService.fetchConsentFlow();
    if (!isCurrent()) {
      return;
    }

    switch (consentFlow) {
      case ConsentFlow.StorageAndNorskPensjon:
        this.navigateWithOnboardingSteps(url, [OnboardingStepKey.NorskPensjonAndStorageConsent]);
        return;
      case ConsentFlow.GroupAndMarketing:
        this.openGroupAndMarketingConsents(url);
        return;
      default:
        this.router.navigate([url], {
          queryParamsHandling: "merge",
        });
    }
  }

  private navigateWithOnboardingSteps(url: string, stepKeys: OnboardingStepKey[]): void {
    this.onboardingService.open(
      {
        onComplete: () =>
          this.router.navigate([url], {
            queryParamsHandling: "merge",
          }),
      },
      stepKeys,
    );
  }

  private openGroupAndMarketingConsents(url: string): void {
    this.dialogService.open(GroupAndMarketingConsentsComponent, {
      width: LARGE_DIALOG_WINDOW_WIDTH,
      data: {
        url,
        queryParams: this.router.routerState.root.snapshot.queryParams,
      },
    });
  }
}
